import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';

// Analytics event types
export type EventName = 'page_viewed';

export interface PageViewEvent {
  event_name: 'page_viewed';
  page_name: string;
  referrer?: string;
  timestamp: number;
}

export type AnalyticsEvent = PageViewEvent & {
  creatorId: string;
}

// Analytics collector interface
export interface IAnalyticsCollector {
  trackPageView(pageName: string, referrer?: string): void;
  clearEvents(): void;
  getTrackedEvents(): AnalyticsEvent[];
}

// Firebase Analytics implementation
export class FirebaseAnalyticsCollector implements IAnalyticsCollector {
  trackPageView(pageName: string, referrer?: string): void {
    if (analytics) {
      // Send to Firebase Analytics
      logEvent(analytics, 'page_viewed', {
        page_name: pageName,
        referrer: referrer || document.referrer,
        timestamp: Date.now()
      });
    } else {
      console.warn('Analytics not initialized');
    }
  }

  clearEvents(): void {
    // Firebase collector doesn't store events locally
  }

  getTrackedEvents(): AnalyticsEvent[] {
    // Firebase collector doesn't store events locally
    return [];
  }
}

// Test implementation for Playwright tests
export class TestAnalyticsCollector implements IAnalyticsCollector {
  private events: AnalyticsEvent[] = [];
  private id : string = uuidv4().toString();

  trackPageView(pageName: string, referrer?: string): void {
    const event: AnalyticsEvent = {
      event_name: 'page_viewed',
      page_name: pageName,
      referrer: referrer || 'test_referrer',
      timestamp: Date.now(),
      creatorId: this.id,
    };
    this.events.push(event);
    console.log(`Test collector[${this.id}]: Page view tracked:`, event);
  }

  clearEvents(): void {
    this.events = [];
  }

  getTrackedEvents(): AnalyticsEvent[] {
    return [...this.events];
  }
}

// Main analytics collector that fires events to multiple collectors
export class MultiCollectorAnalytics implements IAnalyticsCollector {
  private collectors: IAnalyticsCollector[] = [];

  constructor(collectors: IAnalyticsCollector[]) {
    this.collectors = collectors;
  }

  trackPageView(pageName: string, referrer?: string): void {
    this.collectors.forEach(collector => {
      collector.trackPageView(pageName, referrer);
    });
  }

  clearEvents(): void {
    this.collectors.forEach(collector => {
      collector.clearEvents();
    });
  }

  getTrackedEvents(): AnalyticsEvent[] {
    // Return events from the first collector that has them (usually the test collector)
    for (const collector of this.collectors) {
      const events = collector.getTrackedEvents();
      if (events.length > 0) {
        return events;
      }
    }
    return [];
  }

  // Get the test collector for testing purposes
  getTestCollector(): TestAnalyticsCollector | undefined {
    return this.collectors.find(
      collector => collector instanceof TestAnalyticsCollector
    ) as TestAnalyticsCollector;
  }
}

// Singleton factory
class AnalyticsCollectorFactory {
  private static instance: MultiCollectorAnalytics | null = null;
  
  static getInstance(): MultiCollectorAnalytics {
    if (!AnalyticsCollectorFactory.instance) {
      // Always create both collectors
      const firebaseCollector = new FirebaseAnalyticsCollector();
      const testCollector = new TestAnalyticsCollector();
      
      AnalyticsCollectorFactory.instance = new MultiCollectorAnalytics([
        firebaseCollector,
        testCollector
      ]);
    }
    
    return AnalyticsCollectorFactory.instance;
  }
}

// Export the singleton instance
export const analyticsCollector = AnalyticsCollectorFactory.getInstance();

// Export the test collector for tests to access
export const testCollector = AnalyticsCollectorFactory.getInstance().getTestCollector();

// Make test collector available globally for Playwright tests
if (typeof window !== 'undefined') {
  (window as any).testCollector = testCollector;
}
